'use client'
import { motion } from 'framer-motion'
import React, { useState } from 'react'
import TourCard, { TourProps } from '@/components/ui/TourCard'
import type { PageUrlMap } from '@/types/blog'

export interface TourListProps {
    tours: TourProps[];
    title?: string;
    showViewToggle?: boolean;
    className?: string;
    languageId?: number;
    language?: string;
    languageCode?: string;
    pageUrls?: PageUrlMap;
    defaultLanguage?: string;
    __pageContext?: {
        languageId?: number;
        language?: string;
        languageCode?: string;
    };
}

export default function TourList({ 
    tours, 
    title = "Tour Packages",
    showViewToggle = true,
    className = '',
    languageId: propLanguageId,
    language: propLanguage,
    languageCode: propLanguageCode,
    pageUrls,
    defaultLanguage = 'en',
    __pageContext
}: TourListProps) {
    const languageId = propLanguageId || __pageContext?.languageId;
    const resolvedLanguageCode = propLanguage || propLanguageCode || __pageContext?.language || __pageContext?.languageCode;
    const [viewType, setViewType] = useState<'box' | 'horizontal'>('box')

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    }

    return (
        <section className={`w-full ${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <motion.h2
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    className="font-bold text-gray-900 text-2xl sm:text-3xl leading-tight"
                >
                    <span data-prop-path="title">{title}</span>
                </motion.h2>
                
                {showViewToggle && (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-600">
                                Showing {tours && tours.length} results
                            </span>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">View:</span>
                                <div className="flex items-center gap-1">
                                    <button type="button"
                                        onClick={() => setViewType('box')}
                                        className={`p-2 rounded-lg transition-colors duration-200 ${
                                            viewType === 'box' 
                                                ? 'bg-red-600 text-white' 
                                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                        }`}
                                        aria-label="Grid view"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M3 3h7v7H3V3zm11 0h7v7h-7V3zm-11 11h7v7H3v-7zm11 0h7v7h-7v-7z"/>
                                        </svg>
                                    </button>
                                    <button type="button"
                                        onClick={() => setViewType('horizontal')}
                                        className={`p-2 rounded-lg transition-colors duration-200 ${
                                            viewType === 'horizontal' 
                                                ? 'bg-red-600 text-white' 
                                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                        }`}
                                        aria-label="List view"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Tour Cards */}
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className={`grid gap-6 ${
                    viewType === 'box' 
                        ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                        : 'grid-cols-1'
                }`}
            >
                {tours && tours.map((tour, index) => (
                    <TourCard
                        key={index}
                        tour={tour}
                        viewType={viewType}
                        index={index}
                        languageId={languageId}
                        language={resolvedLanguageCode}
                        languageCode={resolvedLanguageCode}
                        pageUrls={pageUrls}
                        defaultLanguage={defaultLanguage}
                    />
                ))}
            </motion.div>
        </section>
    )
}
